/**
 * Serviço de API - Administração
 *
 * CONTEXTO:
 * Encapsula chamadas HTTP relacionadas ao painel administrativo.
 * Reutiliza a instância `clienteApi` definida em `servicos/clienteApi.ts`.
 * 
 * RESPONSABILIDADES:
 * - Estatísticas do sistema
 * - Gestão de usuários (listar, resetar senha, alterar role, ativar/desativar)
 * - Gestão de processos (listar, detalhar, excluir)
 * - Consulta de logs
 * - Gestão de arquivos (listar, download, excluir)
 * - Métricas Prometheus e resumo de métricas
 *
 * PADRÕES:
 * - Erros do axios são transformados em mensagens amigáveis
 * - Documentação exhaustiva
 */

import type { AxiosError } from 'axios';
import { clienteApi } from './clienteApi';

// ==================== TIPOS ====================

export interface EstatisticasSistema {
  total_usuarios: number;
  usuarios_ativos: number;
  total_processos: number;
  processos_por_status: Record<string, number>;
  total_arquivos: number;
  espaco_utilizado_mb: number;
  timestamp: string;
}

export interface UsuarioAdmin {
  id: string;
  email: string;
  nome: string;
  empresa: string | null;
  cargo: string | null;
  ativo: boolean;
  role: string;
  data_criacao: string;
  data_atualizacao: string;
}

export interface ProcessoAdmin {
  processo_id: string;
  nome: string;
  usuario_id: string | null;
  usuario_email: string | null;
  status_processamento: string;
  estado_wizard: string;
  criado_em: string;
  atualizado_em: string;
  total_documentos: number;
}

export interface ProcessoDetalheAdmin {
  processo: Record<string, unknown>;
  documentos: Array<Record<string, unknown>>;
  logs_recentes: Array<Record<string, unknown>>;
}

export interface LogEntry {
  _id?: string;
  timestamp: string;
  level: string;
  message: string;
  module?: string;
  extra?: Record<string, unknown>;
}

export interface LogsResponse {
  logs: LogEntry[];
  total: number;
  mensagem?: string;
}

export interface ArquivoInfo {
  nome: string;
  caminho: string;
  tamanho_bytes: number;
  data_modificacao: string;
  tipo: string;
}

export interface ArquivosResponse {
  arquivos: ArquivoInfo[];
  total: number;
}

export interface MetricasResumo {
  sistema: {
    cpu_percent: number;
    memoria_percent: number;
    disco_percent: number;
    memoria_total_gb?: number;
    memoria_usada_gb?: number;
    disco_total_gb?: number;
    disco_usado_gb?: number;
  };
  aplicacao: {
    requisicoes_total: number;
    requisicoes_erro: number;
    taxa_erro?: number;
    uptime_segundos?: number;
  };
  uploads?: {
    total: number;
    erros: number;
    em_processamento: number;
  };
  analises?: {
    total: number;
    sucesso: number;
  };
  timestamp?: string;
  mensagem?: string;
}

// ==================== HELPERS ====================

function mapearErroAxiosParaMensagem(error: AxiosError): string {
  if (error.isAxiosError && error.response) {
    const status = error.response.status;
    const backendMensagem = (error.response.data as { mensagem?: string; detail?: string })?.mensagem 
      || (error.response.data as { mensagem?: string; detail?: string })?.detail;

    if (status === 401) {
      return 'Não autenticado. Faça login novamente.';
    }
    if (status === 403) {
      return backendMensagem || 'Acesso negado. Apenas administradores podem acessar este recurso.';
    }
    if (status === 404) {
      return backendMensagem || 'Recurso não encontrado.';
    }
    if (status >= 500) {
      return backendMensagem || 'Erro no servidor. Tente novamente mais tarde.';
    }
    return backendMensagem || `Erro HTTP ${status}`;
  }

  if (error && error.message) {
    return error.message;
  }

  return 'Erro desconhecido de rede.';
}

// ==================== ENDPOINTS ====================

/**
 * Obtém estatísticas gerais do sistema
 */
export async function obterEstatisticas(): Promise<EstatisticasSistema> {
  try {
    const resposta = await clienteApi.get<EstatisticasSistema>('/api/admin/estatisticas');
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

// ==================== USUÁRIOS ====================

/**
 * Lista todos os usuários do sistema
 */
export async function listarUsuarios(params?: {
  skip?: number;
  limit?: number;
  filtro_role?: string;
  filtro_ativo?: boolean;
}): Promise<UsuarioAdmin[]> {
  try {
    const resposta = await clienteApi.get<UsuarioAdmin[]>('/api/admin/usuarios', { params });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Reseta a senha de um usuário (gera senha temporária)
 */
export async function resetarSenhaUsuario(usuarioId: string, novaSenha: string): Promise<{ mensagem: string }> {
  try {
    const resposta = await clienteApi.post<{ mensagem: string }>('/api/admin/usuarios/reset-senha', {
      usuario_id: usuarioId,
      nova_senha: novaSenha,
    });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Altera o role de um usuário (admin/user)
 */
export async function alterarRoleUsuario(usuarioId: string, novoRole: 'admin' | 'user'): Promise<{ mensagem: string }> {
  try {
    const resposta = await clienteApi.post<{ mensagem: string }>('/api/admin/usuarios/alterar-role', {
      usuario_id: usuarioId,
      novo_role: novoRole,
    });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Ativa ou desativa um usuário
 */
export async function ativarDesativarUsuario(usuarioId: string, ativo: boolean): Promise<{ mensagem: string }> {
  try {
    const resposta = await clienteApi.post<{ mensagem: string }>('/api/admin/usuarios/ativar-desativar', {
      usuario_id: usuarioId,
      ativo,
    });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

// ==================== PROCESSOS ====================

/**
 * Lista todos os processos de todos os usuários
 */
export async function listarProcessos(params?: {
  skip?: number;
  limit?: number;
  filtro_status?: string;
  filtro_usuario_id?: string;
}): Promise<ProcessoAdmin[]> {
  try {
    const resposta = await clienteApi.get<ProcessoAdmin[]>('/api/admin/processos', { params });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Obtém detalhes de um processo específico
 */
export async function obterProcessoDetalhe(processoId: string): Promise<ProcessoDetalheAdmin> {
  try {
    const resposta = await clienteApi.get<ProcessoDetalheAdmin>(`/api/admin/processos/${processoId}`);
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Exclui um processo e seus documentos
 */
export async function excluirProcesso(processoId: string): Promise<{ mensagem: string }> {
  try {
    const resposta = await clienteApi.delete<{ mensagem: string }>(`/api/admin/processos/${processoId}`);
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

// ==================== LOGS ====================

/**
 * Consulta logs do sistema
 */
export async function consultarLogs(params?: {
  nivel?: string;
  modulo?: string;
  data_inicio?: string;
  data_fim?: string;
  pagina?: number;
  limite?: number;
}): Promise<LogsResponse> {
  try {
    const resposta = await clienteApi.get<LogsResponse>('/api/admin/logs', { params });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

// ==================== ARQUIVOS ====================

/**
 * Lista arquivos do sistema (uploads e logs)
 */
export async function listarArquivos(params?: {
  diretorio?: string;
  extensao?: string;
}): Promise<ArquivosResponse> {
  try {
    const resposta = await clienteApi.get<ArquivosResponse>('/api/admin/arquivos', { params });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Faz download de um arquivo
 * @param diretorio - Diretório onde o arquivo está (uploads, logs)
 * @param caminhoArquivo - Caminho relativo do arquivo dentro do diretório
 */
export async function downloadArquivo(diretorio: string, caminhoArquivo: string): Promise<Blob> {
  try {
    const resposta = await clienteApi.get(`/api/admin/arquivos/download/${encodeURIComponent(diretorio)}/${caminhoArquivo}`, {
      responseType: 'blob',
    });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Exclui um arquivo
 * @param diretorio - Diretório onde o arquivo está (uploads, logs)
 * @param caminhoArquivo - Caminho relativo do arquivo dentro do diretório
 */
export async function excluirArquivo(diretorio: string, caminhoArquivo: string): Promise<{ mensagem: string }> {
  try {
    const resposta = await clienteApi.delete<{ mensagem: string }>(`/api/admin/arquivos/${encodeURIComponent(diretorio)}/${caminhoArquivo}`);
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

// ==================== MÉTRICAS ====================

/**
 * Obtém métricas Prometheus em formato texto
 */
export async function obterMetricasPrometheus(): Promise<string> {
  try {
    const resposta = await clienteApi.get<string>('/api/admin/metricas/prometheus', {
      responseType: 'text',
    });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Obtém resumo de métricas em formato JSON
 */
export async function obterMetricasResumo(): Promise<MetricasResumo> {
  try {
    const resposta = await clienteApi.get<MetricasResumo>('/api/admin/metricas/resumo');
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Helper para iniciar download de um arquivo blob
 */
export function iniciarDownloadBlob(blob: Blob, nomeArquivo: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

// ==================== ORGANIZAÇÕES (ADMIN) ====================

export interface OrganizacaoAdmin {
  id: string;
  nome: string;
  slug: string;
  descricao: string | null;
  ativa: boolean;
  data_criacao: string;
  data_atualizacao: string;
  total_membros: number;
  owner_email: string | null;
  owner_nome: string | null;
}

export interface MembroOrganizacaoAdmin {
  id: string;
  usuario_id: string;
  nome_usuario: string;
  email_usuario: string;
  role: 'owner' | 'admin' | 'member';
  data_entrada: string;
  ativo: boolean;
}

export interface DadosCriarOrganizacaoAdmin {
  nome: string;
  descricao?: string;
  owner_usuario_id: string;
}

export interface DadosAdicionarMembroAdmin {
  usuario_id: string;
  role: 'owner' | 'admin' | 'member';
}

/**
 * Lista todas as organizações do sistema (admin)
 */
export async function listarOrganizacoesAdmin(params?: {
  incluir_inativas?: boolean;
  limite?: number;
}): Promise<OrganizacaoAdmin[]> {
  try {
    const resposta = await clienteApi.get<OrganizacaoAdmin[]>('/api/admin/organizacoes', { params });
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Obtém detalhes de uma organização específica (admin)
 */
export async function obterOrganizacaoAdmin(organizacaoId: string): Promise<OrganizacaoAdmin> {
  try {
    const resposta = await clienteApi.get<OrganizacaoAdmin>(`/api/admin/organizacoes/${organizacaoId}`);
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Lista membros de uma organização (admin)
 */
export async function listarMembrosOrganizacaoAdmin(organizacaoId: string): Promise<MembroOrganizacaoAdmin[]> {
  try {
    const resposta = await clienteApi.get<MembroOrganizacaoAdmin[]>(`/api/admin/organizacoes/${organizacaoId}/membros`);
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Cria uma nova organização (admin)
 */
export async function criarOrganizacaoAdmin(dados: DadosCriarOrganizacaoAdmin): Promise<OrganizacaoAdmin> {
  try {
    const resposta = await clienteApi.post<OrganizacaoAdmin>('/api/admin/organizacoes', dados);
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Atualiza uma organização (admin)
 */
export async function atualizarOrganizacaoAdmin(
  organizacaoId: string,
  dados: { nome?: string; descricao?: string; ativa?: boolean }
): Promise<{ mensagem: string }> {
  try {
    const params = new URLSearchParams();
    if (dados.nome !== undefined) params.append('nome', dados.nome);
    if (dados.descricao !== undefined) params.append('descricao', dados.descricao);
    if (dados.ativa !== undefined) params.append('ativa', String(dados.ativa));
    
    const resposta = await clienteApi.put<{ mensagem: string }>(
      `/api/admin/organizacoes/${organizacaoId}?${params.toString()}`
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Exclui uma organização (admin)
 */
export async function excluirOrganizacaoAdmin(
  organizacaoId: string,
  permanente = false
): Promise<{ mensagem: string }> {
  try {
    const resposta = await clienteApi.delete<{ mensagem: string }>(
      `/api/admin/organizacoes/${organizacaoId}?permanente=${permanente}`
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Adiciona um membro a uma organização (admin)
 */
export async function adicionarMembroOrganizacaoAdmin(
  organizacaoId: string,
  dados: DadosAdicionarMembroAdmin
): Promise<MembroOrganizacaoAdmin> {
  try {
    const resposta = await clienteApi.post<MembroOrganizacaoAdmin>(
      `/api/admin/organizacoes/${organizacaoId}/membros`,
      dados
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Altera o role de um membro (admin)
 */
export async function alterarRoleMembroAdmin(
  organizacaoId: string,
  usuarioId: string,
  novoRole: 'owner' | 'admin' | 'member'
): Promise<{ mensagem: string }> {
  try {
    const resposta = await clienteApi.put<{ mensagem: string }>(
      `/api/admin/organizacoes/${organizacaoId}/membros/${usuarioId}`,
      { novo_role: novoRole }
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}

/**
 * Remove um membro de uma organização (admin)
 */
export async function removerMembroOrganizacaoAdmin(
  organizacaoId: string,
  usuarioId: string
): Promise<{ mensagem: string }> {
  try {
    const resposta = await clienteApi.delete<{ mensagem: string }>(
      `/api/admin/organizacoes/${organizacaoId}/membros/${usuarioId}`
    );
    return resposta.data;
  } catch (error) {
    throw new Error(mapearErroAxiosParaMensagem(error as AxiosError));
  }
}
